import React, { useState } from 'react';
import CandidateRating from './CandidateRating';
import { ReferralSubmission, ExperienceLevel, AvailabilityStatus } from '../../types/ReferralTypes';
import { Vacancy } from '../../types/VacancyTypes';
import { formatCurrency } from '../../utils/formatters';
import './CandidateProfileModal.css';

interface CandidateProfileModalProps {
  referral: ReferralSubmission;
  vacancy?: Vacancy;
  onClose: () => void;
  darkMode?: boolean;
}

const EXPERIENCE_LABELS: Record<ExperienceLevel, string> = {
  entry: 'Entry Level',
  mid: 'Mid Level',
  senior: 'Senior',
  lead: 'Lead',
  executive: 'Executive',
};

const AVAILABILITY_LABELS: Record<AvailabilityStatus, string> = {
  immediately: 'Immediately',
  '2_weeks': '2 Weeks',
  '1_month': '1 Month',
  '3_months': '3 Months',
  not_looking: 'Not Actively Looking',
};

const CandidateProfileModal: React.FC<CandidateProfileModalProps> = ({
  referral,
  vacancy,
  onClose,
  darkMode = false,
}) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'history' | 'evaluation'>('overview');
  const profile = referral.candidate_profile;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className={`candidate-profile-modal ${darkMode ? 'dark' : 'light'}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2>Candidate Profile</h2>
          <button className="close-btn" onClick={onClose}>
            <span className="material-icons">close</span>
          </button>
        </div>

        <div className="candidate-header">
          <img
            src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${encodeURIComponent(referral.candidate_email)}`}
            alt={referral.candidate_name}
            className="candidate-large-avatar"
          />
          <div className="candidate-header-info">
            <h3>{referral.candidate_name}</h3>
            <p className="candidate-email">{referral.candidate_email}</p>
            {referral.candidate_linkedin && (
              <p className="candidate-title">
                <a href={referral.candidate_linkedin} target="_blank" rel="noopener noreferrer">
                  LinkedIn Profile
                </a>
              </p>
            )}
            <p className="candidate-experience">
              {referral.type === 'self_apply'
                ? `Applied for ${referral.vacancy_title}`
                : `Referred by ${referral.referrer_name} for ${referral.vacancy_title}`}
            </p>
          </div>
          <div className="candidate-header-rating">
            <CandidateRating
              candidateId={referral.id}
              candidateName={referral.candidate_name}
              showDetails={false}
              darkMode={darkMode}
            />
          </div>
        </div>

        <div className="modal-tabs">
          <button
            className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            Overview
          </button>
          <button
            className={`tab ${activeTab === 'history' ? 'active' : ''}`}
            onClick={() => setActiveTab('history')}
          >
            Application History
          </button>
          <button
            className={`tab ${activeTab === 'evaluation' ? 'active' : ''}`}
            onClick={() => setActiveTab('evaluation')}
          >
            Evaluation
          </button>
        </div>

        <div className="modal-content">
          {activeTab === 'overview' && (
            <div className="overview-content">
              {referral.note && (
                <div className="section">
                  <h4>{referral.type === 'self_apply' ? 'Note to Hiring Team' : "Referrer's Note"}</h4>
                  <p className="candidate-message">{referral.note}</p>
                </div>
              )}

              {/* Professional Profile */}
              {profile && (
                <div className="section">
                  <h4>Professional Profile</h4>

                  {profile.current_role && (
                    <div className="profile-detail-row">
                      <span className="profile-detail-label">Current Role</span>
                      <span className="profile-detail-value">{profile.current_role}</span>
                    </div>
                  )}

                  {profile.experience_level && (
                    <div className="profile-detail-row">
                      <span className="profile-detail-label">Experience</span>
                      <span className="profile-detail-value experience-badge">
                        {EXPERIENCE_LABELS[profile.experience_level]}
                      </span>
                    </div>
                  )}

                  {profile.skills && profile.skills.length > 0 && (
                    <div className="profile-detail-row">
                      <span className="profile-detail-label">Skills</span>
                      <div className="candidate-skills-chips">
                        {profile.skills.map((skill, i) => (
                          <span key={i} className="skill-chip">{skill}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  {(profile.salary_min || profile.salary_max) && (
                    <div className="profile-detail-row">
                      <span className="profile-detail-label">Salary Range</span>
                      <span className="profile-detail-value">
                        {profile.salary_min && profile.salary_max
                          ? `${formatCurrency(profile.salary_min, profile.salary_currency || 'USD')} - ${formatCurrency(profile.salary_max, profile.salary_currency || 'USD')}`
                          : profile.salary_min
                            ? `From ${formatCurrency(profile.salary_min, profile.salary_currency || 'USD')}`
                            : `Up to ${formatCurrency(profile.salary_max!, profile.salary_currency || 'USD')}`}
                      </span>
                    </div>
                  )}

                  {profile.availability && (
                    <div className="profile-detail-row">
                      <span className="profile-detail-label">Availability</span>
                      <span className="profile-detail-value">{AVAILABILITY_LABELS[profile.availability]}</span>
                    </div>
                  )}

                  {profile.portfolio_url && (
                    <div className="profile-detail-row">
                      <span className="profile-detail-label">Portfolio</span>
                      <span className="profile-detail-value">
                        <a href={profile.portfolio_url} target="_blank" rel="noopener noreferrer">
                          {profile.portfolio_url}
                        </a>
                      </span>
                    </div>
                  )}

                  {profile.cover_letter && (
                    <div className="profile-detail-row cover-letter-row">
                      <span className="profile-detail-label">Cover Letter</span>
                      <p className="cover-letter-text">{profile.cover_letter}</p>
                    </div>
                  )}
                </div>
              )}

              <div className="section">
                <h4>Contact Information</h4>
                <div className="skills-grid">
                  <div className="skill-item">
                    <div className="skill-header">
                      <span className="skill-name">Email</span>
                    </div>
                    <div className="skill-endorsements">{referral.candidate_email}</div>
                  </div>
                  {referral.candidate_linkedin && (
                    <div className="skill-item">
                      <div className="skill-header">
                        <span className="skill-name">LinkedIn</span>
                      </div>
                      <div className="skill-endorsements">
                        <a href={referral.candidate_linkedin} target="_blank" rel="noopener noreferrer">
                          View Profile
                        </a>
                      </div>
                    </div>
                  )}
                  {referral.resume_url && (
                    <div className="skill-item">
                      <div className="skill-header">
                        <span className="skill-name">Resume</span>
                      </div>
                      <div className="skill-endorsements">
                        <a href={referral.resume_url} target="_blank" rel="noopener noreferrer">
                          Download Resume
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="section">
                <h4>Referral Details</h4>
                <div className="skills-grid">
                  <div className="skill-item">
                    <div className="skill-header">
                      <span className="skill-name">Position</span>
                    </div>
                    <div className="skill-endorsements">{referral.vacancy_title}</div>
                  </div>
                  <div className="skill-item">
                    <div className="skill-header">
                      <span className="skill-name">Company</span>
                    </div>
                    <div className="skill-endorsements">{referral.company_name}</div>
                  </div>
                  <div className="skill-item">
                    <div className="skill-header">
                      <span className="skill-name">Submitted</span>
                    </div>
                    <div className="skill-endorsements">
                      {new Date(referral.created_at).toLocaleDateString()}
                    </div>
                  </div>
                  {vacancy && (
                    <div className="skill-item">
                      <div className="skill-header">
                        <span className="skill-name">Bounty</span>
                      </div>
                      <div className="skill-endorsements">
                        {formatCurrency(vacancy.bounty_amount, vacancy.bounty_currency)}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'history' && (
            <div className="history-content">
              <div className="coming-soon-section">
                <div className="coming-soon-icon">
                  <span className="material-icons" style={{ fontSize: '64px' }}>history</span>
                </div>
                <h3>Application History Coming Soon</h3>
                <p>Track this candidate's full application history across all referrals:</p>
                <ul className="feature-list">
                  <li>Previous referrals and outcomes</li>
                  <li>Interview feedback summaries</li>
                  <li>Timeline of all interactions</li>
                  <li>Notes from referrers and hiring managers</li>
                </ul>
              </div>
            </div>
          )}

          {activeTab === 'evaluation' && (
            <div className="evaluation-content">
              <div className="coming-soon-section">
                <div className="coming-soon-icon">
                  <span className="material-icons" style={{ fontSize: '64px' }}>assessment</span>
                </div>
                <h3>Candidate Evaluation Coming Soon</h3>
                <p>Our AI-powered candidate evaluation system will provide:</p>
                <ul className="feature-list">
                  <li>Quality scores based on application history</li>
                  <li>Success rate predictions</li>
                  <li>Risk assessment and recommendations</li>
                  <li>Performance tracking across multiple applications</li>
                </ul>
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="secondary-btn" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default CandidateProfileModal;
